import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { NotesCard, LineItemsView } from "@/components/DetailBits";
import DocumentDialog from "@/components/DocumentDialog";
import { Button } from "@/components/ui/button";
import { ArrowLeft, PencilSimple, PaperPlaneTilt, CheckCircle, ArrowsClockwise, MapPin, User, Copy, SealCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [edit, setEdit] = useState(false);

  const load = () => api.getQuote(id).then(setQuote).catch(() => toast.error("Quote not found"));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  if (!quote) return <div className="p-10 text-slate-400">Loading quote…</div>;

  const send = async () => {
    try {
      const res = await api.sendQuote(id);
      if (res.email?.status === "success") toast.success(`Emailed to ${quote.client_email}`);
      else if (res.email?.status === "skipped") toast.warning("Marked sent (email not configured)");
      else toast.warning("Marked sent");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send email");
    }
  };
  const approve = async () => { await api.setQuoteStatus(id, "approved"); toast.success("Approved"); load(); };
  const convert = async () => { const j = await api.convertQuote(id); toast.success(`Converted to job ${j.number}`); navigate(`/jobs/${j.id}`); };
  const copyApprovalLink = () => { navigator.clipboard.writeText(`${window.location.origin}/approve/${id}`); toast.success("Approval link copied"); };

  return (
    <div className="p-8 max-w-[1100px]">
      <button onClick={() => navigate("/quotes")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4 transition-colors duration-200" data-testid="back-to-quotes"><ArrowLeft size={16} /> Back to Quotes</button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3"><span className="font-mono text-sm font-bold text-[#00B4D8]">{quote.number}</span><StatusBadge status={quote.status} /></div>
          <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight mt-1">Quote for {quote.client_name}</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setEdit(true)} data-testid="edit-quote-button"><PencilSimple size={16} className="mr-1.5" /> Edit</Button>
          <Button variant="outline" onClick={send} data-testid="detail-send-quote"><PaperPlaneTilt size={16} className="mr-1.5" /> Send</Button>
          <Button variant="outline" onClick={copyApprovalLink} data-testid="copy-approval-link"><Copy size={16} className="mr-1.5" /> Approval Link</Button>
          <Button variant="outline" onClick={approve}><CheckCircle size={16} className="mr-1.5" /> Mark Approved</Button>
          <Button onClick={convert} disabled={quote.status === "converted"} className="bg-[#0B1726] hover:bg-slate-800" data-testid="detail-convert-quote"><ArrowsClockwise size={16} className="mr-1.5" /> Convert to Job</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1"><User size={13} weight="duotone" className="text-[#00B4D8]" /> Client</div>
          <Link to={`/clients/${quote.client_id}`} className="text-sm font-medium text-[#00B4D8] hover:underline">{quote.client_name}</Link>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1"><MapPin size={13} weight="duotone" className="text-[#00B4D8]" /> Property</div>
          <div className="text-sm font-medium text-slate-800">{quote.property_address || "—"}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <LineItemsView items={quote.line_items} subtotal={quote.subtotal} tax={quote.tax} taxRate={quote.tax_rate} total={quote.total} doc={quote} />
        </div>
        <NotesCard value={quote.notes} onSave={(t) => api.patchQuoteNotes(id, t)} />
      </div>

      {quote.approved_by_name && (
        <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-lg p-5" data-testid="signed-approval">
          <div className="flex items-center gap-2 mb-3"><SealCheck size={20} weight="fill" className="text-emerald-500" /><h3 className="font-display font-semibold text-slate-900">Customer Approval on File</h3></div>
          <div className="flex flex-wrap items-center gap-6">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Signed by</div>
              <div className="text-sm font-semibold text-slate-800">{quote.approved_by_name}</div>
              {quote.approved_at && <div className="text-xs text-slate-500">{new Date(quote.approved_at).toLocaleString()}</div>}
            </div>
            <div className="bg-white border border-slate-200 rounded-md p-2">
              <img src={api.signatureUrl(id)} alt="Customer signature" className="h-20 object-contain" data-testid="signature-image" />
            </div>
          </div>
        </div>
      )}

      <DocumentDialog open={edit} onClose={() => setEdit(false)} type="quote" initial={quote} onSaved={load} />
    </div>
  );
}
